"use client";

import * as React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { MoreVerticalIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import CommonTableList from "@/components/common/TableList";
import ServiceCreate from "./Create";
import CommonDelete from "@/components/common/Delete";
import ServiceEdit from "./Edit";
import { ServiceData } from "@/interface/entityInterface";
import { ServiceDetails } from "./ServiceDetails";
import { format } from "date-fns";
import CommonList from "@/components/common/List";

export const ServiceList = () => {
  const [open, setOpen] = React.useState(false);
  const [service, setService] = React.useState<ServiceData | null>(null);

  const handleOpen = (e: any, row: ServiceData) => {
    setOpen(true);
    setService(row);
  };

  const columns = React.useMemo<ColumnDef<any>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && "indeterminate")
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
        ),
        enableSorting: false,
        enableHiding: false,
      },
      {
        accessorKey: "id",
        header: "ID",
        cell: ({ row }) => (
          <div className="capitalize">{row.getValue("id")}</div>
        ),
      },
      {
        accessorFn: (row) => row?.vehicle.license_plate || "",
        accessorKey: "vehicle",
        id: "vehicle",
        header: "Vehicle",
        cell: ({ row }) => {
          const vehicle = row.getValue("vehicle") as any;
          return (
            <div className="flex flex-col text-sm space-y-1">
              <span className="font-medium">{vehicle}</span>
            </div>
          );
        },
      },
      {
        accessorKey: "date",
        header: "Date",
        cell: ({ row }) => {
          return (
            <div className="flex flex-col text-sm space-y-1">
              <span className="font-medium">
                {format(row.getValue("date"), "dd MMMM yyyy HH:mm")}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: "odometer",
        header: "Odometer",
        cell: ({ row }) => {
          return (
            <div className="flex flex-col text-sm space-y-1">
              <span className="font-medium">{row.getValue("odometer")}</span>
            </div>
          );
        },
      },
      {
        id: "actions",
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="flex size-8 text-muted-foreground data-[state=open]:bg-muted"
                size="icon"
              >
                <MoreVerticalIcon />
                <span className="sr-only">Open menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-32">
              <ServiceEdit
                id={row.getValue("id")}
                title="Edit"
                description="Edit service"
              />
              <DropdownMenuSeparator />
              <CommonDelete
                title="Delete"
                resource="service"
                id={row.getValue("id")}
              />
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <CommonList>
        <CommonTableList
          columns={columns}
          filterSearch="vehicle"
          create={<ServiceCreate />}
          rowClick={handleOpen}
        />
      </CommonList>
      {open && (
        <ServiceDetails open={open} setOpen={setOpen} service={service} />
      )}
    </>
  );
};

export default ServiceList;
